import React, { useState } from 'react';
// Trigger HMR update
import type { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider, Layout, FloatButton, App as AntdApp } from 'antd';
import { FullscreenExitOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import { SKUList } from './pages/SKUList';
import { SKUDetail } from './pages/SKUDetail';
import { SKUManagement } from './pages/SKUManagement';
import { SKUEdit } from './pages/SKUManagement/SKUEdit';
import FABEEdit from './pages/SKUManagement/FABEEdit';
import CertificationEdit from './pages/SKUManagement/CertificationEdit';
import KeywordEdit from './pages/OperationStrategy/KeywordEdit';
import NegativeKeywordEdit from './pages/OperationStrategy/NegativeKeywordEdit';
import BrandManagement from './pages/BrandManagement';
import { DataManagement } from './pages/DataManagement';
import { DataManagementDetail } from './pages/DataManagementDetail';
import { ABAAnalysisList } from './pages/ABAAnalysisList';
import { ABAAnalysisDetail } from './pages/ABAAnalysisDetail';
import { MetricsGuide } from './pages/MetricsGuide';
import { ConfigManagerPage } from './pages/ConfigManager';
import { TrendAnalysis } from './pages/TrendAnalysis';
import { CompetitorTracker } from './pages/CompetitorTracker';
import DataCheck from './pages/DataCheck';
import BrandCompetition from './pages/BrandCompetition';
import Dashboard from './pages/Dashboard';
import { ShippingManagement } from './pages/ShippingManagement';
import { ShippingChannelEdit } from './pages/ShippingManagement/ShippingChannelEdit';
import RateTierEdit from './pages/ShippingManagement/RateTierEdit';
import ListingCheckItemEdit from './pages/OperationStrategy/ListingCheckItemEdit';
import { ExchangeRateDashboard } from './pages/ExchangeRate/Dashboard';
import PromptManagement from './pages/PromptManagement';
import { OperationStrategyList, OperationStrategyDetail } from './pages/OperationStrategy';
import { KeywordSearchDetail } from './pages/KeywordSearchDetail';
import Register from './pages/Login/Register';
import Login from './pages/Login';
import OrganizationManagement from './pages/OrganizationManagement';
import { MemberDetail } from './pages/OrganizationManagement/components';
import MemberEdit from './pages/OrganizationManagement/MemberEdit';
import DepartmentEdit from './pages/OrganizationManagement/DepartmentEdit';
import InfluencerLibrary from './pages/MarketingManagement/InfluencerLibrary';
import InfluencerEdit from './pages/MarketingManagement/InfluencerEdit';
import InfluencerDetail from './pages/MarketingManagement/InfluencerDetail';
import CompetitorEdit from './pages/CompetitorTracker/CompetitorEdit';

import AdvertisingDashboard from './pages/MarketingManagement/Advertising/AdvertisingDashboard';

// Supply Chain
import StoreManagement from './pages/SupplyChain/StoreManagement';
import { SupplierList, SupplierEdit, PurchaseOrderList, PurchaseOrderDetail } from './pages/SupplyChain/Procurement';
import SupplierDetail from './pages/SupplyChain/Procurement/SupplierDetail';
import Logistics from './pages/SupplyChain/Logistics';
import LogisticsProviders from './pages/SupplyChain/Logistics/LogisticsProviders';
import ShipmentTracker from './pages/SupplyChain/Logistics/ShipmentTracker';
import LogisticsBatchEdit from './pages/SupplyChain/LogisticsBatchEdit';
import SKUMapping from './pages/SupplyChain/SKUMapping';
import SKUMappingEdit from './pages/SupplyChain/SKUMappingEdit';
import DataCenter from './pages/DataAnalysis/DataCenter';
import OrderList from './pages/SupplyChain/OrderList';
import InventoryDashboard from './pages/SupplyChain/InventoryDashboard';
import ReplenishmentDashboard from './pages/SupplyChain/Replenishment/ReplenishmentDashboard';
import ReplenishmentConfigPage from './pages/SupplyChain/Replenishment/ReplenishmentConfig';
import CommunityPostsList from './pages/CommunityPosts';
import FinanceDashboard from './pages/Finance/FinanceDashboard';
import SKUCostConfigPage from './pages/Finance/SKUCostConfigPage';

import { MarketAnalysis } from './pages/MarketAnalysis';

// Administration
import Administration from './pages/Administration';

// 导入认证组件
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';

// 导入新的布局组件
import { Header } from './components/Layout/Header';
import { Sidebar } from './components/Layout/Sidebar';

// 导入新的设计系统
import './styles/global/index.css';
import './App.css';
import { customTheme } from './styles/theme';

const { Content } = Layout;

type AppRoute = {
  path: string;
  element: ReactNode;
};

const primaryRoutes: AppRoute[] = [
  { path: '/', element: <Dashboard /> },
  { path: '/sku-management', element: <SKUManagement /> },
  { path: '/sku-management/edit/:id', element: <SKUEdit /> },
  { path: '/sku-management/:skuId/fabe/new', element: <FABEEdit /> },
  { path: '/sku-management/:skuId/fabe/:id', element: <FABEEdit /> },
  { path: '/sku-management/:skuId/certifications/new', element: <CertificationEdit /> },
  { path: '/sku-management/:skuId/certifications/:id', element: <CertificationEdit /> },
  { path: '/operation-strategy/:skuId/keywords/new', element: <KeywordEdit /> },
  { path: '/operation-strategy/:skuId/keywords/:id', element: <KeywordEdit /> },
  { path: '/operation-strategy/:skuId/negative-keywords/new', element: <NegativeKeywordEdit /> },
  { path: '/operation-strategy/:skuId/negative-keywords/:id', element: <NegativeKeywordEdit /> },
  { path: '/operation-strategy/:skuId/listing-check/new', element: <ListingCheckItemEdit /> },
  { path: '/operation-strategy/:skuId/listing-check/:id', element: <ListingCheckItemEdit /> },
  { path: '/shipping-management', element: <ShippingManagement /> },
  { path: '/shipping-management/new', element: <ShippingChannelEdit /> },
  { path: '/shipping-management/edit/:id', element: <ShippingChannelEdit /> },
  { path: '/shipping-management/:channelId/rate-tiers/new', element: <RateTierEdit /> },
  { path: '/shipping-management/:channelId/rate-tiers/:id', element: <RateTierEdit /> },
  { path: '/exchange-rate/dashboard', element: <ExchangeRateDashboard /> },
  { path: '/operation-strategy', element: <OperationStrategyList /> },
  { path: '/operation-strategy/:id', element: <OperationStrategyDetail /> },
  { path: '/brand-management', element: <BrandManagement /> },
  { path: '/data-management', element: <DataManagement /> },
  { path: '/data-management/:id', element: <DataManagementDetail /> },
  { path: '/analysis', element: <ABAAnalysisList /> },
  { path: '/analysis/:id', element: <ABAAnalysisDetail /> },
  { path: '/analysis/:id/config', element: <ConfigManagerPage /> },
  { path: '/analysis/:id/trends', element: <TrendAnalysis /> },
  { path: '/analysis/:id/competitors', element: <CompetitorTracker /> },
  { path: '/data-check', element: <DataCheck /> },
  { path: '/brand-competition', element: <BrandCompetition /> },
  { path: '/metrics-guide', element: <MetricsGuide /> },
  { path: '/config-manager', element: <ConfigManagerPage /> },
  { path: '/prompt-management', element: <PromptManagement /> },
  { path: '/keyword-search/:id', element: <KeywordSearchDetail /> },
  { path: '/organization', element: <OrganizationManagement /> },
  { path: '/organization/members/new', element: <MemberEdit /> },
  { path: '/organization/members/:id', element: <MemberDetail /> },
  { path: '/organization/departments/new', element: <DepartmentEdit /> },
  { path: '/operation-strategy/:skuId/competitors/new', element: <CompetitorEdit /> },
  // Marketing Management
  { path: '/marketing/influencers', element: <InfluencerLibrary /> },
  { path: '/marketing/influencers/new', element: <InfluencerEdit /> },
  { path: '/marketing/influencers/:id', element: <InfluencerDetail /> },
  // Supply Chain
  { path: '/supply-chain/stores', element: <StoreManagement /> },
  { path: '/supply-chain/procurement/suppliers', element: <SupplierList /> },
  { path: '/supply-chain/procurement/suppliers/new', element: <SupplierEdit /> },
  { path: '/supply-chain/procurement/suppliers/:id/view', element: <SupplierDetail /> },
  { path: '/supply-chain/procurement/suppliers/:id', element: <SupplierEdit /> },
  { path: '/supply-chain/procurement/orders', element: <PurchaseOrderList /> },
  { path: '/supply-chain/procurement/orders/new', element: <PurchaseOrderDetail /> },
  { path: '/supply-chain/procurement/orders/:id', element: <PurchaseOrderDetail /> },
  { path: '/supply-chain/logistics', element: <Logistics /> },
  { path: '/supply-chain/logistics/tracker', element: <ShipmentTracker /> },
  { path: '/supply-chain/logistics/providers', element: <LogisticsProviders /> },
  { path: '/supply-chain/logistics/batches/new', element: <LogisticsBatchEdit /> },
  { path: '/supply-chain/logistics/batches/:id', element: <LogisticsBatchEdit /> },
  { path: '/supply-chain/replenishment', element: <ReplenishmentDashboard /> },
  { path: '/supply-chain/replenishment/config', element: <ReplenishmentConfigPage /> },
  { path: '/supply-chain/sku-mapping', element: <SKUMapping /> },
  { path: '/supply-chain/sku-mapping/new', element: <SKUMappingEdit /> },
  { path: '/supply-chain/sku-mapping/:id', element: <SKUMappingEdit /> },
  { path: '/supply-chain/orders', element: <OrderList /> },
  { path: '/supply-chain/inventory', element: <InventoryDashboard /> },
  { path: '/data-analysis/center', element: <DataCenter /> },
  // Administration
  { path: '/administration', element: <Administration /> },
  // Community Posts
  { path: '/community-posts', element: <CommunityPostsList /> },
  // Finance
  { path: '/finance/dashboard', element: <FinanceDashboard /> },
  { path: '/finance/cost-config', element: <SKUCostConfigPage /> },
];

const legacyRoutes: AppRoute[] = [
  { path: '/aba-analysis', element: <MarketAnalysis /> },
  { path: '/aba-analysis/:id', element: <ABAAnalysisDetail /> },
  { path: '/skus', element: <SKUList /> },
  { path: '/skus/:id', element: <SKUDetail /> },
  { path: '/skus/:id/config', element: <ConfigManagerPage /> },
  { path: '/skus/:id/trends', element: <TrendAnalysis /> },
  { path: '/skus/:id/competitors', element: <CompetitorTracker /> },
];

const appRoutes: AppRoute[] = [...primaryRoutes, ...legacyRoutes];

const AppRoutes: React.FC = () => (
  <Routes>
    {appRoutes.map(({ path, element }) => (
      <Route key={path} path={path} element={element} />
    ))}
  </Routes>
);

const AppChrome: React.FC = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);

  return (
    <Layout className={`app-shell ${isFullscreen ? 'app-shell--fullscreen' : ''}`}>
      {!isFullscreen && <Header onFullscreenToggle={toggleFullscreen} isFullscreen={isFullscreen} />}
      <Layout className="app-shell__body">
        {!isFullscreen && <Sidebar />}
        <Content className="app-shell__content">
          <div className="app-shell__surface">
            <AppRoutes />
          </div>
        </Content>
      </Layout>
      {isFullscreen && (
        <FloatButton
          icon={<FullscreenExitOutlined />}
          tooltip="退出全屏"
          type="primary"
          style={{ right: 24, bottom: 24 }}
          onClick={toggleFullscreen}
        />
      )}
    </Layout>
  );
};

function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={customTheme}
    >
      <AntdApp>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <Routes>
              {/* 登录页 - 无需认证 */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              <Route path="marketing">
                <Route path="influencers" element={<InfluencerLibrary />} />
                <Route path="influencers/:id" element={<InfluencerDetail />} />
                <Route path="advertising" element={<AdvertisingDashboard />} />
              </Route>
              {/* 主应用 - 需要认证 */}
              <Route
                path="/*"
                element={
                  <PrivateRoute>
                    <AppChrome />
                  </PrivateRoute>
                }
              />
            </Routes>
          </AuthProvider>
        </Router>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;


